import type {
  EmbedLoomBlockUrlData,
  EmbedLoomModel,
} from '@blocksuite/affine-model';
import type { LinkPreviewProvider } from '@blocksuite/affine-shared/services';
import { isAbortError } from '@blocksuite/affine-shared/utils';

import type { EmbedLoomBlockComponent } from './embed-loom-block.js';

const LoomOEmbedEndpoint = 'https://www.loom.com/v1/oembed';

export async function queryEmbedLoomData(
  embedLoomModel: EmbedLoomModel,
  linkPreviewer: LinkPreviewProvider,
  signal?: AbortSignal
): Promise<Partial<EmbedLoomBlockUrlData>> {
  const url = embedLoomModel.props.url;

  const loomEmbedData: Partial<EmbedLoomBlockUrlData> =
    await queryLoomOEmbedData(url, linkPreviewer, signal);

  return loomEmbedData;
}

export async function queryLoomOEmbedData(
  url: string,
  linkPreviewer: LinkPreviewProvider,
  signal?: AbortSignal
): Promise<Partial<EmbedLoomBlockUrlData>> {
  let loomOEmbedData: Partial<EmbedLoomBlockUrlData> = {};

  const oEmbedUrl = `${LoomOEmbedEndpoint}?url=${encodeURIComponent(url)}`;

  const oEmbedJson = await linkPreviewer.queryOEmbed(oEmbedUrl, signal);
  if (oEmbedJson) {
    const {
      title,
      description,
      thumbnail_url: image,
    } = oEmbedJson as {
      title?: string;
      description?: string;
      thumbnail_url?: string;
    };

    loomOEmbedData = {
      title,
      description,
      image,
    };
  }

  return loomOEmbedData;
}

export async function refreshEmbedLoomUrlData(
  embedLoomElement: EmbedLoomBlockComponent,
  signal?: AbortSignal
): Promise<void> {
  let title = null,
    description = null,
    image = null;

  try {
    embedLoomElement.loading = true;

    const queryUrlData = embedLoomElement.service?.queryUrlData;
    if (!queryUrlData) return;

    const loomUrlData = await queryUrlData(embedLoomElement.model);
    ({ title = null, description = null, image = null } = loomUrlData);

    if (signal?.aborted) return;

    embedLoomElement.store.updateBlock(embedLoomElement.model, {
      title,
      description,
      image,
    });
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) return;
  } finally {
    embedLoomElement.loading = false;
  }
}
